import { computed, readonly, ref } from "vue";

import { api, whenUnauthenticated } from "../api/client";
import type { User } from "../api/types";

/**
 * Who is signed in, for the whole admin.
 *
 * Module scope on purpose: there is exactly one session per browser tab, and
 * the header, the router and every screen have to agree on it.
 */
const user = ref<User | null>(null);

/** True until the first look at the server has come back. */
const checking = ref(true);

/**
 * The session went away while the admin was open.
 *
 * Kept apart from "not signed in" because the answer is different: the screen
 * stays as it is and a dialog asks for the password, so a half-filled form is
 * still there afterwards.
 */
const expired = ref(false);

/**
 * Who was signed in when it happened.
 *
 * Kept separately because `user` is cleared in the same breath, and the dialog
 * that asks for the password again reads this a tick later - by then there
 * would be nothing left to prefill the username with.
 */
const expiredFor = ref("");

whenUnauthenticated(() => {
  if (user.value) {
    expiredFor.value = user.value.username;
    expired.value = true;
  }
  user.value = null;
});

let starting: Promise<void> | null = null;

/**
 * Ask the server who we are, once.
 *
 * Signed in, this also brings back the CSRF token. Signed out, it fetches one,
 * because the sign-in itself needs a token.
 */
function start(): Promise<void> {
  starting ??= (async () => {
    try {
      const session = await api.session();
      user.value = session.user;
    } catch {
      user.value = null;
      try {
        await api.csrfToken();
      } catch {
        // No token and no session: the sign-in will report the real problem.
      }
    } finally {
      checking.value = false;
    }
  })();

  return starting;
}

async function signIn(username: string, password: string): Promise<void> {
  const session = await api.signIn(username, password);
  user.value = session.user;
  expired.value = false;
}

async function signOut(): Promise<void> {
  await api.signOut();
  user.value = null;
  expired.value = false;
}

export function useSession() {
  return {
    user: readonly(user),
    checking: readonly(checking),
    expired: readonly(expired),
    expiredFor: readonly(expiredFor),
    isSignedIn: computed(() => user.value !== null),
    start,
    signIn,
    signOut,
  };
}

/** Test seam: the module-level state outlives a single component test. */
export function resetSessionForTest(): void {
  user.value = null;
  checking.value = true;
  expired.value = false;
  expiredFor.value = "";
  starting = null;
}
